/**
 * Map DRIVER_ON_VEHICLE graph edges into operator-facing assignment rows
 * for Driver Compliance expanded detail.
 *
 * Core mapping is the twin of zeecore
 * `driversSummaryVehiclesDriven.mapEdgesToVehiclesDriven` — keep behaviour in sync.
 */

export interface DriverOnVehicleEdgeLike {
  relationship_type?: string;
  from_entity_id?: string;
  to_entity_id?: string;
  from_ts?: string;
  to_ts?: string | null;
}

/** Same shape as zeecore `VehicleDrivenAssignment`. */
export interface DriverVehicleAssignmentRow {
  vehicle_entity_id: string;
  vehicle_label: string;
  start: string;
  /** null when assignment is still open */
  end: string | null;
  open_ended: boolean;
  /** Hours from start to end (or to clampTo when open), 1 decimal. */
  duration_hours: number;
}

/** Cached graph payload for Vehicles driven — remap durations with a fresh clamp on hit. */
export interface VehiclesDrivenCacheEntry {
  edges: DriverOnVehicleEdgeLike[];
  entityLabels: Record<string, string>;
  entityIds: string[];
  error: string | null;
}

function parseTs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function shortEntityLabel(entityId: string): string {
  if (entityId.length <= 12) return entityId;
  return `${entityId.slice(0, 8)}…`;
}

/**
 * Cache key for period-scoped vehicles-driven lookups.
 * Intentionally excludes clamp/"now" so live Today re-expands remapping
 * open durations without another graph scan.
 */
export function vehiclesDrivenCacheKey(params: {
  driverId: string;
  activityEntityId?: string | null;
  from: string;
  to: string;
}): string {
  const activity = String(params.activityEntityId ?? '').trim();
  return [
    String(params.driverId ?? '').trim(),
    activity,
    params.from,
    params.to,
  ].join('|');
}

/**
 * Prefer registration labels; fall back to a short entity id.
 */
export function vehicleLabelForAssignment(
  vehicleEntityId: string,
  entityLabels?: Record<string, string> | null,
): string {
  const label = entityLabels?.[vehicleEntityId]?.trim();
  if (label) return label;
  return shortEntityLabel(vehicleEntityId);
}

/**
 * Entity ids to query for DRIVER_ON_VEHICLE when Aquarius display id and
 * Samsara activity id have not been merged yet.
 */
export function driverEntityIdsForAssignmentLookup(driver: {
  driver_id: string;
  activity_entity_id?: string | null;
}): string[] {
  const ids = [driver.driver_id, driver.activity_entity_id]
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);
  return [...new Set(ids)];
}

/**
 * Twin of zeecore `mapEdgesToVehiclesDriven` — keep in sync.
 */
export function mapEdgesToVehiclesDriven(
  edges: DriverOnVehicleEdgeLike[],
  options: {
    driverEntityIds: string[];
    entityLabels?: Record<string, string> | null;
    clampToIso: string;
  },
): DriverVehicleAssignmentRow[] {
  const clampMs = parseTs(options.clampToIso) ?? Date.now();
  const driverIds = new Set(
    options.driverEntityIds.map((id) => String(id ?? '').trim()).filter(Boolean),
  );
  const rows: DriverVehicleAssignmentRow[] = [];
  const seen = new Set<string>();

  for (const edge of edges) {
    if (edge.relationship_type && edge.relationship_type !== 'DRIVER_ON_VEHICLE') continue;

    let vehicleId: string | undefined;
    if (edge.from_entity_id && driverIds.has(edge.from_entity_id)) {
      vehicleId = edge.to_entity_id;
    } else if (edge.to_entity_id && driverIds.has(edge.to_entity_id)) {
      vehicleId = edge.from_entity_id;
    } else {
      continue;
    }
    if (!vehicleId || !edge.from_ts) continue;

    const startMs = parseTs(edge.from_ts);
    if (startMs == null) continue;

    const openEnded = edge.to_ts == null || String(edge.to_ts).trim() === '';
    const endMs = openEnded ? clampMs : parseTs(edge.to_ts);
    if (endMs == null) continue;

    const durationHours = Math.round((Math.max(0, endMs - startMs) / 3_600_000) * 10) / 10;
    const key = `${vehicleId}|${edge.from_ts}|${edge.to_ts ?? 'open'}`;
    if (seen.has(key)) continue;
    seen.add(key);

    rows.push({
      vehicle_entity_id: vehicleId,
      vehicle_label: vehicleLabelForAssignment(vehicleId, options.entityLabels),
      start: edge.from_ts,
      end: openEnded ? null : String(edge.to_ts),
      open_ended: openEnded,
      duration_hours: durationHours,
    });
  }

  rows.sort((a, b) => a.start.localeCompare(b.start) || a.vehicle_label.localeCompare(b.vehicle_label));
  return rows;
}

/**
 * Build sorted assignment rows from graph edges for a driver entity.
 * Duration for open-ended edges is clamped to `clampToIso` (usually the
 * selected period end, or "now" when viewing live today).
 */
export function mapDriverOnVehicleEdgesToAssignments(
  edges: DriverOnVehicleEdgeLike[],
  options: {
    driverEntityId: string;
    /** Extra entity ids for the same person (e.g. Samsara sibling before merge). */
    siblingEntityIds?: string[];
    entityLabels?: Record<string, string> | null;
    /** ISO end used when to_ts is null (open assignment). */
    clampToIso: string;
  },
): DriverVehicleAssignmentRow[] {
  return mapEdgesToVehiclesDriven(edges, {
    driverEntityIds: [options.driverEntityId, ...(options.siblingEntityIds ?? [])],
    entityLabels: options.entityLabels,
    clampToIso: options.clampToIso,
  });
}

/**
 * Remap open-ended assignment durations to a fresh clamp (e.g. live "now"
 * when viewing Today). Closed windows are left unchanged.
 */
export function remapVehiclesDrivenClamp(
  rows: DriverVehicleAssignmentRow[],
  clampToIso: string,
): DriverVehicleAssignmentRow[] {
  const clampMs = parseTs(clampToIso) ?? Date.now();
  return rows.map((row) => {
    if (!row.open_ended) return row;
    const startMs = parseTs(row.start);
    if (startMs == null) return row;
    const durationHours = Math.round((Math.max(0, clampMs - startMs) / 3_600_000) * 10) / 10;
    return { ...row, duration_hours: durationHours };
  });
}

/**
 * Convert summary-attached vehicles_driven rows into edge-like payloads so
 * the client cache can remap via mapDriverOnVehicleEdgesToAssignments.
 */
export function vehiclesDrivenRowsToEdgeLikes(
  rows: DriverVehicleAssignmentRow[],
  driverEntityId: string,
): DriverOnVehicleEdgeLike[] {
  return rows.map((row) => ({
    relationship_type: 'DRIVER_ON_VEHICLE',
    from_entity_id: driverEntityId,
    to_entity_id: row.vehicle_entity_id,
    from_ts: row.start,
    to_ts: row.open_ended ? null : row.end,
  }));
}
